import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        product_id: product.id,
        product_name: product.name,
      });
      toast.success("Enquiry sent. Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="product-notfound">
        <p className="font-serif text-3xl">Piece not found.</p>
        <Link to="/collection" className="text-primary hover-underline">Back to collection</Link>
      </div>
    );

  if (!product)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="product-loading">Loading…</div>;

  const specs = [
    ["Material", product.material],
    ["Dimensions", product.dimensions],
    ["Category", product.category],
    ["Availability", product.in_stock ? "Made to order" : "Enquire for lead time"],
  ].filter(([, v]) => v);

  return (
    <div data-testid="product-detail-page" className="pt-28 md:pt-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-10">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
          {/* Image — spotlight framed */}
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="relative"
          >
            <div className="sticky top-28 overflow-hidden border border-border bg-card aspect-[4/5]">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
            </div>
          </motion.div>

          {/* Details */}
          <div className="lg:py-4">
            <Reveal>
              <p className="text-xs tracking-[0.4em] uppercase text-primary mb-4">{product.category}</p>
              <h1 className="font-serif font-light text-4xl md:text-6xl leading-tight">{product.name}</h1>
              <p className="text-primary text-2xl mt-6">{formatPrice(product.price)}</p>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mt-8 max-w-xl">{product.description}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <dl className="mt-10 border-t border-border">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-6 py-4 border-b border-border text-sm">
                    <dt className="text-muted-foreground tracking-wide uppercase text-xs">{k}</dt>
                    <dd className="text-foreground text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            {/* Inquiry form */}
            <Reveal delay={0.15}>
              <div className="mt-12 border border-border bg-card p-6 md:p-8">
                <h2 className="font-serif text-2xl font-light mb-1">Enquire about this piece</h2>
                <p className="text-sm text-muted-foreground mb-6">We&apos;ll confirm availability, finishes and delivery.</p>
                <form onSubmit={submitInquiry} className="space-y-4" data-testid="product-inquiry-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input required placeholder="Your name" data-testid="inquiry-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border-border" />
                    <Input required type="email" placeholder="Email" data-testid="inquiry-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border-border" />
                  </div>
                  <Input placeholder="Phone (optional)" data-testid="inquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" />
                  <Textarea required placeholder="Your message" rows={3} data-testid="inquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-background border-border" />
                  <Button type="submit" disabled={submitting} data-testid="inquiry-submit" className="w-full bg-primary text-primary-foreground hover:opacity-90 rounded-none h-12">
                    {submitting ? "Sending…" : (<span className="inline-flex items-center gap-2"><Check size={16} /> Send Enquiry</span>)}
                  </Button>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
